import { useLayoutEffect, useRef, type RefObject } from 'react';
import type { editor as monacoEditor, IDisposable } from 'monaco-editor';
import {
  getKeybindingLayoutMap,
  subscribeEffectiveKeymap,
} from '../../../keybindings/dispatch/effectiveKeymap';
import {
  registerKeybindingTarget,
  type KeybindingEditor,
  type KeybindingHandler,
  type KeybindingHandlersRef,
} from '../../../keybindings/dispatch/targetRegistry';
import { getShortcutLabel } from '../../../keybindings/hooks/useShortcutLabel';

export const FORMAT_SQL_KEYBINDING_COMMAND_IDS = ['query.formatSql'] as const;

export type FormatSqlActionHost = {
  readonly addAction: (descriptor: monacoEditor.IActionDescriptor) => IDisposable;
  /** Every Monaco editor has it; SqlMonaco's narrowed editor type just does not declare it. */
  readonly onDidDispose?: (listener: () => void) => IDisposable;
};

export function formatSqlActionLabel(shortcutLabel: string): string {
  return shortcutLabel ? `Format SQL (${shortcutLabel})` : 'Format SQL';
}

/**
 * The editor context-menu "Format SQL" entry. Monaco shows no shortcut for it
 * (the dispatcher, not Monaco, owns the chord), so the label carries the
 * registry's current chord and is re-registered whenever the effective keymap
 * or the keyboard layout changes. Released with the editor.
 */
export function registerFormatSqlContextAction(
  editor: FormatSqlActionHost,
  run: (editor: monacoEditor.ICodeEditor) => void,
): IDisposable {
  let action: IDisposable | null = null;
  let label: string | null = null;
  const register = () => {
    const next = formatSqlActionLabel(getShortcutLabel(FORMAT_SQL_KEYBINDING_COMMAND_IDS[0]));
    if (next === label) return;
    action?.dispose();
    label = next;
    action = editor.addAction({
      id: 'format-sql',
      label: next,
      contextMenuGroupId: 'modification',
      contextMenuOrder: 1.5,
      run,
    });
  };
  register();
  const unsubscribeKeymap = subscribeEffectiveKeymap(register);
  const unsubscribeLayout = getKeybindingLayoutMap().subscribe(register);
  const release = () => {
    unsubscribeKeymap();
    unsubscribeLayout();
  };
  // The editor does not own what addAction registered: its disposable holds
  // global command and menu registrations, so dispose it with the editor.
  const editorDisposed = editor.onDidDispose?.(() => {
    release();
    action?.dispose();
    action = null;
  });
  return {
    dispose: () => {
      release();
      editorDisposed?.dispose();
      action?.dispose();
      action = null;
    },
  };
}

type UseFormatSqlKeybindingOptions = {
  readonly editorRef: RefObject<KeybindingEditor | null>;
  readonly rootRef: RefObject<HTMLElement | null>;
  readonly onFormat: KeybindingHandler;
};

function commonAncestor(left: Element, right: Element): Element | null {
  const leftAncestors = new Set<Element>();
  for (let current: Element | null = left; current; current = current.parentElement) {
    leftAncestors.add(current);
  }
  for (let current: Element | null = right; current; current = current.parentElement) {
    if (leftAncestors.has(current)) return current;
  }
  return null;
}

function keybindingRootForEditor(editorRoot: HTMLElement | null): Element | null {
  if (!editorRoot) return null;
  const chrome = document.activeElement?.closest('[data-keybinding-chrome]');
  return chrome ? commonAncestor(editorRoot, chrome) ?? editorRoot : editorRoot;
}

export function useFormatSqlKeybinding({
  editorRef,
  rootRef,
  onFormat,
}: UseFormatSqlKeybindingOptions): void {
  const onFormatRef = useRef(onFormat);
  onFormatRef.current = onFormat;
  const handlersRef = useRef<KeybindingHandlersRef>({ current: {} });
  handlersRef.current.current = {
    [FORMAT_SQL_KEYBINDING_COMMAND_IDS[0]]: () => onFormatRef.current(),
  };

  useLayoutEffect(() => registerKeybindingTarget({
    scope: 'sql-editor',
    handlers: handlersRef.current,
    getEditor: () => editorRef.current,
    getRootElement: () => keybindingRootForEditor(rootRef.current),
    canExecute: () => editorRef.current !== null,
  }), [editorRef, rootRef]);
}
